import {
    Avatar,
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    Icon,
    Pagination,
    SearchBar,
    Text,
    type ColorType,
    type IconProps,
} from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fullName, hasName, initials, jobAndDepartmentLabel, MUTED, NAVY } from './personDisplay';
import {
    EmployeeRuleSelector,
    type EmployeeRuleSelectorHandle,
    type RuleSelectionSummaryItem,
    type RuleSelectorPersistedState,
} from '../profiles/EmployeeRuleSelector';

import type { EmployeePickerFilterParams } from '@/api/competency-services/profile-assignment';
import type { EmployeePickerItem } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { CompanyManager } from '@/types/directory/Company';

import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import {
    EMPLOYEE_PAGE_SIZE,
    useGetCompanyEmployees,
} from '@/api/competency-services/profile-assignment';
import profileAssignmentService from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { useGetCompanyInformation } from '@/api/directory';
import { queryClient } from '@/AppWithProviders';
import { CompanyAttributeFilters } from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { ProfileAssignmentQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { type SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';
import { getProfilePictureUrl } from '@/utils/user';

// Maps the lifted company-attribute filter selection to the employee picker query's id
// arrays. Mirrors `toRosterParams` from CompetencyProfilesAdminPage.
const toEmployeePickerFilterParams = (filters: SelectedFilters): EmployeePickerFilterParams => {
    const ids = (items: Array<{ id?: string }>) =>
        items.length > 0
            ? items.map((item) => item.id).filter((id): id is string => Boolean(id))
            : undefined;

    return {
        departmentIds: ids(filters.departments),
        jobTitleIds: ids(filters.jobTitles),
        managerIds: ids(filters.managers as CompanyManager[]),
        companyAddressIds: ids(filters.companyAddresses),
        employeeLevelIds: ids(filters.employeeLevels),
        customAttributeIds: ids(filters.customAttributes),
    };
};

export type WizardSelectionSummary =
    | { method: 'individual'; count: number; alreadyCount: number }
    | { method: 'rule'; rule: RuleSelectionSummaryItem[]; count: number; alreadyCount: number }
    | {
          method: 'profile';
          profileNames: string[];
          count: number;
          alreadyCount: number;
          memberCount: number;
      };

export type AssignToEmployeesWizardProps = {
    title: string;
    subtitle?: string;
    competencyName?: string;
    confirmLabel: string | ((count: number) => string);
    confirmProfileLabel?: (profileCount: number) => string;
    successMessage: (count: number) => string;
    successProfileMessage?: (profileCount: number) => string;
    onConfirm: (employeeIds: string[]) => Promise<void>;
    onConfirmProfileMethod?: (profileIds: string[]) => Promise<void>;
    onClose: () => void;
    excludeProfileId?: string;
    isSubmitting?: boolean;
    resolveAlreadyAssignedIds?: (employeeIds: string[]) => Promise<string[]>;
    alreadyAssignedNote?: (count: number) => string;
    alreadyAssignedClause?: (count: number) => string;
    describeSelection?: (summary: WizardSelectionSummary) => string;
    disabledEmployeeIds?: Set<string>;
    disabledEmployeeLabel?: string;
    competencyId?: string;
    groupId?: string;
};

type AssignMethod = 'individual' | 'rule' | 'profile';

type WizardStep = 'method' | 'detail' | 'review';

const PANEL = '#f5f7f9';
const BORDER = '#dbe0e6';
const ERROR = 'var(--color-error)';

export const AssignToEmployeesWizard = ({
    title,
    subtitle,
    competencyName,
    confirmLabel,
    confirmProfileLabel,
    successMessage,
    successProfileMessage,
    onConfirm,
    onConfirmProfileMethod,
    onClose,
    excludeProfileId,
    isSubmitting = false,
    resolveAlreadyAssignedIds,
    alreadyAssignedNote,
    alreadyAssignedClause,
    describeSelection,
    disabledEmployeeIds,
    disabledEmployeeLabel,
    competencyId,
    groupId,
}: AssignToEmployeesWizardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();
    const { getJobTitle, getDepartment } = useCompanyInformation();
    const { data: company } = useGetCompanyInformation();

    const jobAndDepartment = useCallback(
        (employee: EmployeePickerItem): string =>
            jobAndDepartmentLabel(employee, {
                getJobTitle,
                getDepartment,
                fallback: t('competencies.assignToEmployees.individual.noJobDepartment'),
            }),
        [getJobTitle, getDepartment, t],
    );

    const isProfileLinkMethod = Boolean(onConfirmProfileMethod);

    const [step, setStep] = useState<WizardStep>('method');
    const [method, setMethod] = useState<AssignMethod | null>(null);

    const [search, setSearch] = useState('');
    const [individualFilters, setIndividualFilters] = useState<SelectedFilters>();
    const [employeePage, setEmployeePage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    // Server-side paging swaps the fetched page, so picked employees are kept in
    // this id -> item map to still render them in the review step.
    const [selectedEmployeesById, setSelectedEmployeesById] = useState<
        Map<string, EmployeePickerItem>
    >(() => new Map());
    const [filterKey] = useState(() => Date.now());

    const [sourceProfileIds, setSourceProfileIds] = useState<string[]>([]);
    const [searchProfile, setSearchProfile] = useState('');
    const [profileMemberCounts, setProfileMemberCounts] = useState<Record<string, number>>({});

    const ruleSelectorRef = useRef<EmployeeRuleSelectorHandle>(null);
    const [ruleMatchedCount, setRuleMatchedCount] = useState(0);
    const [ruleSelectorState, setRuleSelectorState] = useState<RuleSelectorPersistedState>();
    const [ruleReviewAttempted, setRuleReviewAttempted] = useState(false);
    const hasAnyRuleSelected = useMemo(
        () =>
            Boolean(
                ruleSelectorState?.ruleCards?.some(
                    (c) => c.attribute && c.valueIds && c.valueIds.length > 0,
                ),
            ),
        [ruleSelectorState],
    );

    const [resolvedIds, setResolvedIds] = useState<string[]>([]);
    const [isResolving, setIsResolving] = useState(false);

    const [reviewRuleSummary, setReviewRuleSummary] = useState<RuleSelectionSummaryItem[]>([]);
    const [reviewProfileNames, setReviewProfileNames] = useState<string[]>([]);
    const [reviewProfileIds, setReviewProfileIds] = useState<string[]>([]);
    const [reviewProfiles, setReviewProfiles] = useState<Array<{ name: string; members: number }>>(
        [],
    );
    const [reviewProfileMemberCount, setReviewProfileMemberCount] = useState(0);
    const [reviewMethod, setReviewMethod] = useState<AssignMethod | null>(null);
    const [alreadyAssignedCount, setAlreadyAssignedCount] = useState(0);

    const individualFilterParams = useMemo<EmployeePickerFilterParams>(
        () => (individualFilters ? toEmployeePickerFilterParams(individualFilters) : {}),
        [individualFilters],
    );

    // CompanyAttributeFilters re-emits onFiltersChange from a useEffect whenever it (re)mounts or
    // its callback identity changes, so the page reset is guarded on the serialized
    // params: only a real filter-content change goes back to page 1.
    const lastFilterParamsKey = useRef('');
    const handleIndividualFiltersChange = useCallback((filters: SelectedFilters) => {
        const paramsKey = JSON.stringify(toEmployeePickerFilterParams(filters));
        if (paramsKey !== lastFilterParamsKey.current) {
            lastFilterParamsKey.current = paramsKey;
            setEmployeePage(1);
        }
        setIndividualFilters(filters);
    }, []);

    const {
        data: employeeData,
        isFetching: isEmployeesFetching,
        isError: isEmployeesError,
    } = useGetCompanyEmployees(
        search,
        method === 'individual',
        individualFilterParams,
        competencyId,
        groupId,
        employeePage,
    );

    const employeeTotalCount = employeeData?.totalCount ?? 0;
    const employeeTotalPages = Math.max(1, Math.ceil(employeeTotalCount / EMPLOYEE_PAGE_SIZE));

    const employees = useMemo(() => {
        const items = employeeData?.items ?? [];
        const query = search.trim().toLowerCase();
        if (!query) {
            return items;
        }

        return items.filter((employee) => {
            const haystack = `${fullName(employee)} ${employee.emailAddress}`.toLowerCase();

            return haystack.includes(query);
        });
    }, [employeeData?.items, search]);

    const selectedEmployees = useMemo(
        () => Array.from(selectedEmployeesById.values()),
        [selectedEmployeesById],
    );

    const { data: allProfiles } = useGetCompetencyProfiles(
        method === 'profile',
        competencyId,
        groupId,
    );

    const sourceProfiles = useMemo(
        () => (allProfiles ?? []).filter((candidate) => candidate.id !== excludeProfileId),
        [allProfiles, excludeProfileId],
    );

    const filteredSourceProfiles = useMemo(() => {
        const rows = sourceProfiles.map((candidate) => ({
            id: candidate.id,
            name: getTextByLanguage(candidate.name, language),
            description: getTextByLanguage(candidate.description, language),
            skillCount: candidate.items.length,
            isAlreadyAssigned: candidate.isAlreadyAssigned,
        }));

        const query = searchProfile.trim().toLowerCase();
        if (!query) {
            return rows;
        }

        return rows.filter(
            (row) =>
                row.name.toLowerCase().includes(query) ||
                row.description.toLowerCase().includes(query),
        );
    }, [sourceProfiles, language, searchProfile]);

    const fetchProfileAssigneeIds = useCallback(async (profileId: string): Promise<string[]> => {
        const assignments = await queryClient.fetchQuery({
            queryKey: [ProfileAssignmentQueryKey.ASSIGNMENTS, profileId],
            queryFn: () => profileAssignmentService.getAssignments(profileId),
            staleTime: 0,
        });

        return assignments.map((assignment) => assignment.employeeId);
    }, []);

    useEffect(() => {
        if (method !== 'profile') {
            return;
        }

        const missing = filteredSourceProfiles
            .map((row) => row.id)
            .filter((id) => profileMemberCounts[id] === undefined);

        if (missing.length === 0) {
            return;
        }

        let cancelled = false;
        void Promise.all(
            missing.map(async (id) => [id, (await fetchProfileAssigneeIds(id)).length] as const),
        ).then((entries) => {
            if (cancelled) {
                return;
            }

            setProfileMemberCounts((current) => {
                const next = { ...current };
                entries.forEach(([id, count]) => {
                    next[id] = count;
                });

                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [method, filteredSourceProfiles, profileMemberCounts, fetchProfileAssigneeIds]);

    // Auto-check profiles that already contain the competency/group
    useEffect(() => {
        if (method === 'profile' && allProfiles) {
            const preCheckedIds = allProfiles
                .filter((profile) => profile.isAlreadyAssigned)
                .map((profile) => profile.id);

            if (preCheckedIds.length > 0) {
                setSourceProfileIds((current) => {
                    const merged = new Set([...current, ...preCheckedIds]);
                    return Array.from(merged);
                });
            }
        }
    }, [method, allProfiles]);

    const toggleEmployee = (employee: EmployeePickerItem) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(employee.id)) {
                next.delete(employee.id);
            } else {
                next.add(employee.id);
            }

            return next;
        });
        setSelectedEmployeesById((current) => {
            const next = new Map(current);
            if (next.has(employee.id)) {
                next.delete(employee.id);
            } else {
                next.set(employee.id, employee);
            }

            return next;
        });
    };

    const toggleSourceProfile = (profileId: string) => {
        // Prevent unchecking pre-checked profiles
        const profile = allProfiles?.find((p) => p.id === profileId);
        if (profile?.isAlreadyAssigned) {
            return;
        }

        setSourceProfileIds((current) =>
            current.includes(profileId)
                ? current.filter((id) => id !== profileId)
                : [...current, profileId],
        );
    };

    const methodCards: Array<{
        value: AssignMethod;
        icon: IconProps['name'];
        label: string;
        description: string;
    }> = [
        {
            value: 'individual',
            icon: 'users-user',
            label: t('competencies.assignToEmployees.method.individual.label'),
            description: t('competencies.assignToEmployees.method.individual.description'),
        },
        {
            value: 'rule',
            icon: 'users-profile-2user',
            label: t('competencies.assignToEmployees.method.rule.label'),
            description: t('competencies.assignToEmployees.method.rule.description'),
        },
        {
            value: 'profile',
            icon: 'users-user-profile-add-2',
            label: t('competencies.assignToEmployees.method.profile.label'),
            description: t('competencies.assignToEmployees.method.profile.description'),
        },
    ];

    const resolveProfileMethodIds = async (): Promise<string[]> => {
        const lists = await Promise.all(sourceProfileIds.map(fetchProfileAssigneeIds));

        const ids = new Set<string>();
        lists.forEach((employeeIds) => employeeIds.forEach((id) => ids.add(id)));

        return Array.from(ids);
    };

    const canProceedFromDetail = (): boolean => {
        switch (method) {
            case 'individual':
                return selectedIds.size > 0;
            case 'rule':
                return hasAnyRuleSelected && ruleMatchedCount > 0;
            case 'profile':
                return sourceProfileIds.length > 0;
            default:
                return false;
        }
    };

    const handleReviewClick = async () => {
        // For rule-based method, show validation only when attempting to proceed.
        if (method === 'rule' && !canProceedFromDetail()) {
            setRuleReviewAttempted(true);
            return;
        }

        await goToReview();
    };

    const selectMethod = (value: AssignMethod) => {
        setMethod(value);
        setStep('detail');
    };

    const goToReview = async () => {
        setIsResolving(true);
        try {
            let ids: string[] = [];
            if (method === 'individual') {
                ids = Array.from(selectedIds);
                setReviewRuleSummary([]);
                setReviewProfileNames([]);
                setReviewProfiles([]);
                setReviewProfileMemberCount(0);
            } else if (method === 'rule') {
                ids = (await ruleSelectorRef.current?.resolveEmployeeIds()) ?? [];
                setReviewRuleSummary(ruleSelectorRef.current?.getSelectionSummary() ?? []);
                setReviewProfileNames([]);
                setReviewProfiles([]);
                setReviewProfileMemberCount(0);
            } else {
                setReviewRuleSummary([]);
                setReviewProfileIds([...sourceProfileIds]);

                const selected = sourceProfiles.filter((profile) =>
                    sourceProfileIds.includes(profile.id),
                );
                const withMembers = await Promise.all(
                    selected.map(async (profile) => ({
                        name: getTextByLanguage(profile.name, language),
                        members:
                            profileMemberCounts[profile.id] ??
                            (await fetchProfileAssigneeIds(profile.id)).length,
                    })),
                );

                setReviewProfileNames(withMembers.map((entry) => entry.name));
                setReviewProfiles(withMembers);

                if (isProfileLinkMethod) {
                    ids = [];
                    setReviewProfileMemberCount(
                        withMembers.reduce((sum, entry) => sum + entry.members, 0),
                    );
                } else {
                    ids = await resolveProfileMethodIds();
                    setReviewProfileMemberCount(ids.length);
                }
            }

            let alreadyCount = 0;
            if (resolveAlreadyAssignedIds && ids.length > 0) {
                try {
                    const already = await resolveAlreadyAssignedIds(ids);
                    alreadyCount = already.filter((id) => ids.includes(id)).length;
                } catch {
                    alreadyCount = 0;
                }
            }

            setAlreadyAssignedCount(alreadyCount);
            setReviewMethod(method);
            setResolvedIds(ids);
            setStep('review');
        } catch (error) {
            handleError({ error });
        } finally {
            setIsResolving(false);
        }
    };

    const isProfileLinkReview = isProfileLinkMethod && reviewMethod === 'profile';

    const canConfirm = isProfileLinkReview ? reviewProfileIds.length > 0 : resolvedIds.length > 0;

    const handleConfirm = async () => {
        if (!canConfirm || isSubmitting) {
            return;
        }

        try {
            if (isProfileLinkReview) {
                await onConfirmProfileMethod!(reviewProfileIds);
            } else {
                await onConfirm(resolvedIds);
            }

            const successText = isProfileLinkReview
                ? (successProfileMessage ?? successMessage)(reviewProfileIds.length)
                : successMessage(resolvedIds.length);
            showSuccessToast({ message: successText });
            onClose();
        } catch (error) {
            handleError({ error });
        }
    };

    const isBusy = isSubmitting || isResolving;

    const labelCount = isProfileLinkReview ? reviewProfileIds.length : resolvedIds.length;
    const resolvedConfirmLabel =
        isProfileLinkReview && confirmProfileLabel
            ? confirmProfileLabel(reviewProfileIds.length)
            : typeof confirmLabel === 'function'
              ? confirmLabel(labelCount)
              : confirmLabel;

    const reviewSelectionDescription = (): string => {
        if (describeSelection && reviewMethod) {
            const summary: WizardSelectionSummary =
                reviewMethod === 'rule'
                    ? {
                          method: 'rule',
                          rule: reviewRuleSummary,
                          count: resolvedIds.length,
                          alreadyCount: alreadyAssignedCount,
                      }
                    : reviewMethod === 'profile'
                      ? {
                            method: 'profile',
                            profileNames: reviewProfileNames,
                            count: resolvedIds.length,
                            alreadyCount: alreadyAssignedCount,
                            memberCount: reviewProfileMemberCount,
                        }
                      : {
                            method: 'individual',
                            count: resolvedIds.length,
                            alreadyCount: alreadyAssignedCount,
                        };

            return describeSelection(summary);
        }

        if (reviewMethod === 'profile' && reviewProfileNames.length > 0) {
            return t('competencies.assignToEmployees.review.profileDescription', {
                profiles: reviewProfileNames.join(', '),
                count: reviewProfileNames.length,
            });
        }

        const willReceive =
            reviewMethod === 'rule'
                ? t('competencies.assignToEmployees.review.willReceiveRule', {
                      count: resolvedIds.length,
                  })
                : t('competencies.assignToEmployees.review.willReceiveIndividual', {
                      count: resolvedIds.length,
                  });

        if (alreadyAssignedCount > 0) {
            return t('competencies.assignToEmployees.review.willReceiveWithAlready', {
                sentence: willReceive,
                already:
                    alreadyAssignedClause?.(alreadyAssignedCount) ??
                    t('competencies.assignToEmployees.review.alreadyHaveClause', {
                        count: alreadyAssignedCount,
                    }),
            });
        }

        return willReceive;
    };

    return (
        <div className={'-m-6 flex max-h-[88vh] flex-col'}>
            <div className={'px-8 pt-8 pb-0'}>
                <p
                    className={'text-center text-[20px] leading-6 font-semibold md:!text-left'}
                    style={{ color: NAVY }}
                >
                    {title}
                </p>

                {subtitle && (
                    <p
                        className={'mt-2 text-center text-[14px] leading-5 md:!text-left'}
                        style={{ color: MUTED }}
                    >
                        {subtitle}
                    </p>
                )}

                <div className={'border-primary mt-4 border-b'} />
            </div>

            <div className={'scrollbar-glass min-h-[320px] flex-1 overflow-y-auto px-8 py-8'}>
                {step === 'method' && (
                    <div className={'flex flex-col gap-6 py-2'}>
                        <p
                            className={'text-[18px] leading-[22px] font-semibold'}
                            style={{ color: NAVY }}
                        >
                            {t('competencies.assignToEmployees.method.heading')}
                        </p>

                        <div className={'grid grid-cols-1 gap-4 md:grid-cols-3'}>
                            {methodCards.map((card) => {
                                const isSelected = method === card.value;

                                return (
                                    <button
                                        key={card.value}
                                        type={'button'}
                                        onClick={() => selectMethod(card.value)}
                                        className={
                                            'group flex min-h-[178px] flex-col items-center justify-center gap-4 rounded-2xl border bg-white p-6 text-center transition-all ' +
                                            (isSelected
                                                ? 'border-primary shadow-md'
                                                : 'hover:border-primary/50 border-[#dbe0e6] hover:shadow-md')
                                        }
                                        data-testid={`assign-method-${card.value}`}
                                    >
                                        <span
                                            className={
                                                'bg-primary/10 flex h-16 w-16 items-center justify-center rounded-xl'
                                            }
                                        >
                                            <Icon
                                                name={card.icon}
                                                size={26}
                                                color={Colors.PRIMARY}
                                            />
                                        </span>

                                        <span className={'flex flex-col gap-1'}>
                                            <span
                                                className={'text-[14px] leading-5 font-semibold'}
                                                style={{ color: NAVY }}
                                            >
                                                {card.label}
                                            </span>

                                            <span
                                                className={'text-[12px] leading-4'}
                                                style={{ color: MUTED }}
                                            >
                                                {card.description}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {step === 'detail' && method === 'individual' && (
                    <div className={'flex flex-col gap-6'}>
                        <div
                            className={'flex flex-col gap-5 rounded-2xl p-6'}
                            style={{ backgroundColor: PANEL }}
                        >
                            <div className={'flex items-center gap-3'}>
                                <Icon
                                    name={'search-search-normal-1'}
                                    color={NAVY as ColorType}
                                />

                                <p
                                    className={'text-lg font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {t('competencies.assignToEmployees.individual.heading')}
                                </p>

                                <span
                                    className={
                                        'bg-primary/10 ml-auto shrink-0 rounded-full px-3 py-1 text-xs font-semibold text-[#0a8c80]'
                                    }
                                    data-testid={'assign-employees-selected-count'}
                                >
                                    {t('competencies.assignToEmployees.individual.selectedCount', {
                                        count: selectedIds.size,
                                    })}
                                </span>
                            </div>

                            <div className={'grid grid-cols-[1fr_auto] items-center gap-3'}>
                                <SearchBar
                                    size={'sm'}
                                    value={search}
                                    onChange={(value) => {
                                        setSearch(value);
                                        setEmployeePage(1);
                                    }}
                                    placeholder={t(
                                        'competencies.assignToEmployees.individual.searchPlaceholder',
                                    )}
                                    dataTestId={'assign-employees-search'}
                                />

                                <CompanyAttributeFilters
                                    key={filterKey}
                                    company={company}
                                    onFiltersChange={handleIndividualFiltersChange}
                                    inline={true}
                                    placement={'bottom-right'}
                                />
                            </div>
                        </div>

                        {/* p-2 keeps the tiles' overflowing selected-badge (-top-2/-right-2) inside the scroll box. */}
                        <div className={'scrollbar-glass max-h-[350px] overflow-y-auto p-2'}>
                            {isEmployeesFetching && employees.length === 0 ? (
                                <div className={'flex h-[280px] items-center justify-center'}>
                                    <OverlayLoader isLoading={true} />
                                </div>
                            ) : isEmployeesError ? (
                                <p
                                    className={'py-12 text-center text-base'}
                                    style={{ color: ERROR }}
                                >
                                    {t('competencies.assignToEmployees.individual.loadError')}
                                </p>
                            ) : employees.length === 0 ? (
                                <p
                                    className={'py-12 text-center text-base'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.assignToEmployees.individual.empty')}
                                </p>
                            ) : (
                                <div className={'grid grid-cols-3 gap-3'}>
                                    {employees.map((employee) => {
                                        // Use isAlreadyAssigned from API response, fallback to disabledEmployeeIds prop
                                        const isDisabled =
                                            employee.isAlreadyAssigned ||
                                            (disabledEmployeeIds?.has(employee.id) ?? false);
                                        const isSelected = selectedIds.has(employee.id);

                                        return (
                                            <button
                                                key={employee.id}
                                                type={'button'}
                                                onClick={() =>
                                                    !isDisabled && toggleEmployee(employee)
                                                }
                                                disabled={isDisabled}
                                                className={
                                                    'relative flex items-center gap-3 rounded-xl border p-4 text-left transition-all ' +
                                                    (isDisabled
                                                        ? 'bg-base-50 cursor-default border-[#dbe0e6] opacity-60'
                                                        : isSelected
                                                          ? 'border-primary bg-primary/5 shadow-sm'
                                                          : 'hover:border-primary/30 border-[#dbe0e6] bg-white')
                                                }
                                                data-testid={`assign-employees-employee-${employee.id}`}
                                            >
                                                {isSelected && !isDisabled && (
                                                    <span
                                                        className={
                                                            'bg-primary absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full shadow-sm'
                                                        }
                                                        data-testid={`assign-employees-employee-${employee.id}-selected`}
                                                    >
                                                        <Icon
                                                            name={'essential-checkmark'}
                                                            size={'sm'}
                                                            color={Colors.WHITE}
                                                        />
                                                    </span>
                                                )}

                                                <Avatar
                                                    size={'sm'}
                                                    bgColor={'default'}
                                                    initials={initials(employee)}
                                                    src={
                                                        getProfilePictureUrl(
                                                            employee.profilePictureUrl,
                                                        ) || undefined
                                                    }
                                                    alt={fullName(employee)}
                                                />

                                                <div className={'min-w-0'}>
                                                    <p
                                                        className={
                                                            'truncate text-base font-semibold'
                                                        }
                                                        style={{ color: NAVY }}
                                                    >
                                                        {hasName(employee)
                                                            ? fullName(employee)
                                                            : t('common.anonymous')}
                                                    </p>

                                                    {isDisabled && disabledEmployeeLabel ? (
                                                        <span
                                                            className={
                                                                'bg-secondary-100 text-secondary-700 mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium'
                                                            }
                                                        >
                                                            {disabledEmployeeLabel}
                                                        </span>
                                                    ) : (
                                                        <p
                                                            className={'truncate text-sm'}
                                                            style={{ color: MUTED }}
                                                        >
                                                            {jobAndDepartment(employee)}
                                                        </p>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {!isEmployeesError && employeeTotalCount > 0 && (
                            <Pagination
                                variant={'default'}
                                currentPage={employeePage}
                                totalPages={employeeTotalPages}
                                totalCount={employeeTotalCount}
                                pageSize={EMPLOYEE_PAGE_SIZE}
                                isLoading={isEmployeesFetching}
                                onPageChange={setEmployeePage}
                                dataTestId={'assign-employees-pagination'}
                            />
                        )}
                    </div>
                )}

                {step === 'detail' && method === 'rule' && (
                    <div
                        className={'flex flex-col gap-6 rounded-2xl p-7'}
                        style={{ backgroundColor: PANEL }}
                    >
                        <div className={'flex items-center gap-3'}>
                            <Icon
                                name={'essential-slider'}
                                color={NAVY as ColorType}
                            />

                            <div>
                                <p
                                    className={'text-lg font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {t('competencies.assignToEmployees.rule.heading')}
                                </p>

                                <p
                                    className={'text-base'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.assignToEmployees.rule.description')}
                                </p>
                            </div>
                        </div>

                        <EmployeeRuleSelector
                            ref={ruleSelectorRef}
                            onMatchedCountChange={setRuleMatchedCount}
                            initialState={ruleSelectorState}
                            onStateChange={setRuleSelectorState}
                            highlightEmpty={ruleReviewAttempted && !hasAnyRuleSelected}
                        />
                    </div>
                )}

                {step === 'detail' && method === 'profile' && (
                    <div className={'flex flex-col gap-6'}>
                        <div
                            className={'flex flex-col gap-4 rounded-2xl p-6'}
                            style={{ backgroundColor: PANEL }}
                        >
                            <div className={'flex items-center gap-3'}>
                                <Icon
                                    name={'users-user-profile-add-2'}
                                    color={NAVY as ColorType}
                                />

                                <p
                                    className={'text-lg font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {t('competencies.assignToEmployees.profile.heading')}
                                </p>
                            </div>

                            <Text
                                size={'default'}
                                color={Colors.BASE_500}
                            >
                                {t('competencies.assignToEmployees.profile.helper')}
                            </Text>

                            <SearchBar
                                size={'sm'}
                                value={searchProfile}
                                onChange={(value) => setSearchProfile(value)}
                                placeholder={t(
                                    'competencies.assignToEmployees.profile.searchPlaceholder',
                                )}
                                dataTestId={'assign-profiles-search'}
                            />
                        </div>

                        <div
                            className={'rounded-xl border'}
                            style={{ borderColor: BORDER }}
                        >
                            {filteredSourceProfiles.length === 0 ? (
                                <p
                                    className={'py-12 text-center text-base'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.assignToEmployees.profile.empty')}
                                </p>
                            ) : (
                                filteredSourceProfiles.map((profile) => {
                                    const isSelected = sourceProfileIds.includes(profile.id);
                                    const isPreChecked = profile.isAlreadyAssigned ?? false;

                                    return (
                                        <button
                                            key={profile.id}
                                            type={'button'}
                                            onClick={() => toggleSourceProfile(profile.id)}
                                            disabled={isPreChecked}
                                            className={
                                                'flex w-full items-center gap-4 p-4 text-left transition-colors ' +
                                                (isPreChecked
                                                    ? 'bg-primary/5 cursor-default opacity-75'
                                                    : isSelected
                                                      ? 'bg-primary/5 hover:bg-primary/10'
                                                      : 'bg-white hover:bg-[#f5f7f9]')
                                            }
                                            data-testid={`assign-profile-${profile.id}`}
                                        >
                                            <Checkbox
                                                checked={isSelected || isPreChecked}
                                                disabled={isPreChecked}
                                                ariaLabel={profile.name}
                                                className={'pointer-events-none'}
                                            />

                                            <div className={'min-w-0 flex-1'}>
                                                <div className={'flex items-center gap-2'}>
                                                    <p
                                                        className={'text-base font-semibold'}
                                                        style={{ color: NAVY }}
                                                    >
                                                        {profile.name}
                                                    </p>
                                                </div>

                                                {profile.description && (
                                                    <p
                                                        className={'truncate text-sm'}
                                                        style={{ color: MUTED }}
                                                    >
                                                        {profile.description}
                                                    </p>
                                                )}

                                                <p
                                                    className={
                                                        'text-primary mt-0.5 text-sm font-medium'
                                                    }
                                                >
                                                    {t(
                                                        'competencies.assignToEmployees.profile.skillCount',
                                                        { count: profile.skillCount },
                                                    )}
                                                </p>
                                            </div>

                                            <span
                                                className={
                                                    'bg-primary/10 shrink-0 rounded-full px-3 py-1 text-xs font-semibold text-[#0a8c80]'
                                                }
                                                data-testid={`assign-profile-members-${profile.id}`}
                                            >
                                                {profileMemberCounts[profile.id] === undefined
                                                    ? '…'
                                                    : t(
                                                          'competencies.assignToEmployees.profile.memberCount',
                                                          {
                                                              count: profileMemberCounts[
                                                                  profile.id
                                                              ],
                                                          },
                                                      )}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {step === 'review' && (
                    <div className={'flex flex-col gap-6'}>
                        <div
                            className={'flex flex-col gap-4 rounded-2xl p-6'}
                            style={{ backgroundColor: PANEL }}
                        >
                            <p
                                className={'text-base font-semibold'}
                                style={{ color: NAVY }}
                            >
                                {t('competencies.assignToEmployees.review.competencyHeading')}
                            </p>

                            <div
                                className={'rounded-xl border bg-white p-4'}
                                style={{ borderColor: BORDER }}
                            >
                                <p
                                    className={'font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {competencyName ?? title}
                                </p>
                            </div>
                        </div>

                        <div
                            className={'flex flex-col gap-4 rounded-2xl p-6'}
                            style={{ backgroundColor: PANEL }}
                        >
                            <div className={'flex items-center gap-2'}>
                                <Icon
                                    name={
                                        isProfileLinkReview
                                            ? 'users-user-profile-add-2'
                                            : 'users-people'
                                    }
                                    color={NAVY as ColorType}
                                />

                                <p
                                    className={'text-base font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {isProfileLinkReview
                                        ? t('competencies.assignToEmployees.review.profilesHeading')
                                        : t(
                                              'competencies.assignToEmployees.review.employeesHeading',
                                          )}
                                </p>
                            </div>

                            {isProfileLinkReview ? (
                                <>
                                    <div
                                        className={'rounded-xl border bg-white p-4'}
                                        style={{ borderColor: BORDER }}
                                    >
                                        <p
                                            className={'text-base'}
                                            style={{ color: NAVY }}
                                        >
                                            {reviewSelectionDescription()}
                                        </p>

                                        {alreadyAssignedCount > 0 && (
                                            <p
                                                className={'mt-2 text-base'}
                                                style={{ color: MUTED }}
                                            >
                                                {alreadyAssignedNote?.(alreadyAssignedCount) ??
                                                    t(
                                                        'competencies.assignToEmployees.review.alreadyAssigned',
                                                        { count: alreadyAssignedCount },
                                                    )}
                                            </p>
                                        )}
                                    </div>

                                    {reviewProfiles.length > 0 && (
                                        <div className={'flex flex-col gap-3'}>
                                            {reviewProfiles.map((profile) => (
                                                <div
                                                    key={profile.name}
                                                    className={
                                                        'flex items-center justify-between rounded-xl border bg-white p-4'
                                                    }
                                                    style={{ borderColor: BORDER }}
                                                >
                                                    <p
                                                        className={'text-base font-semibold'}
                                                        style={{ color: NAVY }}
                                                    >
                                                        {profile.name}
                                                    </p>

                                                    <span
                                                        className={
                                                            'text-primary text-sm font-medium'
                                                        }
                                                    >
                                                        {t(
                                                            'competencies.assignToEmployees.profile.memberCountCurrent',
                                                            { count: profile.members },
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : resolvedIds.length === 0 ? (
                                <p
                                    className={'py-6 text-center text-base'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.assignToEmployees.review.empty')}
                                </p>
                            ) : (
                                <>
                                    <div
                                        className={'rounded-xl border bg-white p-4'}
                                        style={{ borderColor: BORDER }}
                                    >
                                        <p
                                            className={'text-base'}
                                            style={{ color: NAVY }}
                                        >
                                            {reviewSelectionDescription()}
                                        </p>

                                        {reviewMethod === 'profile' && alreadyAssignedCount > 0 && (
                                            <p
                                                className={'mt-2 text-base'}
                                                style={{ color: MUTED }}
                                            >
                                                {t(
                                                    'competencies.assignToEmployees.review.alreadyAssigned',
                                                    { count: alreadyAssignedCount },
                                                )}
                                            </p>
                                        )}
                                    </div>

                                    {reviewMethod === 'individual' &&
                                        selectedEmployees.length > 0 && (
                                            <div className={'flex flex-wrap gap-3'}>
                                                {selectedEmployees.map((employee) => (
                                                    <div
                                                        key={employee.id}
                                                        className={
                                                            'flex items-center gap-3 rounded-xl border bg-white p-3 pr-5'
                                                        }
                                                        style={{ borderColor: BORDER }}
                                                    >
                                                        <Avatar
                                                            size={'sm'}
                                                            bgColor={'default'}
                                                            initials={initials(employee)}
                                                            src={
                                                                getProfilePictureUrl(
                                                                    employee.profilePictureUrl,
                                                                ) || undefined
                                                            }
                                                            alt={fullName(employee)}
                                                        />

                                                        <div>
                                                            <p
                                                                className={
                                                                    'text-base font-semibold'
                                                                }
                                                                style={{ color: NAVY }}
                                                            >
                                                                {fullName(employee)}
                                                            </p>

                                                            <p
                                                                className={'text-sm'}
                                                                style={{ color: MUTED }}
                                                            >
                                                                {employee.emailAddress}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                    {reviewMethod === 'profile' && reviewProfiles.length > 0 && (
                                        <div className={'flex flex-col gap-3'}>
                                            <p
                                                className={'text-base font-semibold'}
                                                style={{ color: NAVY }}
                                            >
                                                {t(
                                                    'competencies.assignToEmployees.review.profilesHeading',
                                                )}
                                            </p>

                                            {reviewProfiles.map((profile) => (
                                                <div
                                                    key={profile.name}
                                                    className={
                                                        'flex items-center justify-between rounded-xl border bg-white p-4'
                                                    }
                                                    style={{ borderColor: BORDER }}
                                                >
                                                    <p
                                                        className={'text-base font-semibold'}
                                                        style={{ color: NAVY }}
                                                    >
                                                        {profile.name}
                                                    </p>

                                                    <span
                                                        className={
                                                            'text-primary text-sm font-medium'
                                                        }
                                                    >
                                                        {t(
                                                            'competencies.assignToEmployees.profile.memberCount',
                                                            { count: profile.members },
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div
                className={'flex items-center justify-end gap-3 border-t px-8 py-5'}
                style={{ borderColor: BORDER }}
            >
                {step !== 'method' && (
                    <ButtonSecondary
                        onClick={() => setStep(step === 'review' ? 'detail' : 'method')}
                        disabled={isBusy}
                        size={'md'}
                        dataTestId={'assign-employees-back'}
                        className={'mr-auto text-sm'}
                        fitContent
                    >
                        {t('competencies.assignToEmployees.actions.back')}
                    </ButtonSecondary>
                )}

                <ButtonSecondary
                    onClick={onClose}
                    disabled={isBusy}
                    size={'md'}
                    dataTestId={'assign-employees-cancel'}
                    className={'text-sm'}
                    fitContent={true}
                >
                    {t('competencies.assignToEmployees.actions.cancel')}
                </ButtonSecondary>

                {step === 'method' && (
                    <ButtonGradientPrimary
                        onClick={() => method && setStep('detail')}
                        disabled={!method}
                        size={'md'}
                        dataTestId={'assign-employees-next'}
                        className={'text-sm'}
                        fitContent={true}
                    >
                        {t('competencies.assignToEmployees.actions.next')}
                    </ButtonGradientPrimary>
                )}

                {step === 'detail' && (
                    <ButtonGradientPrimary
                        onClick={() => void handleReviewClick()}
                        loading={isResolving}
                        disabled={method !== 'rule' && !canProceedFromDetail()}
                        size={'md'}
                        dataTestId={'assign-employees-review'}
                        className={'text-sm'}
                        fitContent={true}
                    >
                        {t('competencies.assignToEmployees.actions.review')}
                    </ButtonGradientPrimary>
                )}

                {step === 'review' && (
                    <ButtonGradientPrimary
                        onClick={() => void handleConfirm()}
                        loading={isSubmitting}
                        disabled={!canConfirm || isSubmitting}
                        size={'md'}
                        dataTestId={'assign-employees-confirm'}
                        className={'text-sm'}
                        fitContent={true}
                    >
                        {resolvedConfirmLabel}
                    </ButtonGradientPrimary>
                )}
            </div>
        </div>
    );
};
